import React, { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useInView } from 'framer-motion'
import { Else, If, Then } from 'react-if'
import { ResponsiveObject } from 'react-slick'
import { Image } from '@/atoms/Image'
import { AsH1, TitleLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { getWebClient } from '@/services/ApolloClient'
import { fetchNowStreamingProjects } from '@/services/GuildSignupPageService'
import { NowStreamingContent } from '@/services/NowStreamingService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

const defaultResponsiveSlick: ResponsiveObject[] = [
  {
    breakpoint: 2900,
    settings: {
      slidesToScroll: 9,
      slidesToShow: 9,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToScroll: 7,
      slidesToShow: 7,
    },
  },
  {
    breakpoint: 2048,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
]

export const GuildNowStreamingRail: React.FC<{
  featuredSlugs?: string[]
  title?: string
  landing?: boolean
  initialProjects?: NowStreamingContent[]
}> = ({ featuredSlugs, title, landing = false, initialProjects }) => {
  const { t } = useTranslate('guild')
  const router = useSafeRouter()
  const { locale } = useLocale()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })
  const hasInitialProjects = initialProjects && initialProjects.length > 0
  const [isLoaded, setIsLoaded] = useState(hasInitialProjects)
  const [nowStreamingProjects, setNowStreamingProjects] = useState<NowStreamingContent[]>(initialProjects ?? [])

  useEffect(() => {
    if (isLoaded || !inView) return

    const client = getWebClient({ locale })
    fetchNowStreamingProjects(client, featuredSlugs)
      .then(setNowStreamingProjects)
      .finally(() => setIsLoaded(true))
  }, [isLoaded, featuredSlugs, locale, inView])

  const handleCardClick = useCallback(
    (item: NowStreamingContent) => {
      if (!landing) return
      if (!item) return

      const slug = item.project?.slug
      router.push(`${paths.watch.index}/${slug}`)
    },
    [landing, router],
  )

  if (isLoaded && !nowStreamingProjects?.length) return null

  return (
    <div ref={ref}>
      <header>
        <TitleLG
          color="white"
          weight="bold"
          as={AsH1}
          className={classNames('mb-3 md:mb-4', { 'photon-title-md !mb-8': landing })}
        >
          <If condition={title}>
            <Then>{title}</Then>
            <Else>{landing ? t('newReleases', 'New Releases') : t('nowStreaming', 'Now Streaming')}</Else>
          </If>
        </TitleLG>
      </header>

      <HorizontalItemScroller responsive={defaultResponsiveSlick} arrows>
        {!isLoaded
          ? Array(7)
              .fill(0)
              .map((v, i) => (
                <PlaceholderMediaCard
                  className="mr-4"
                  borderRadiusClassName="rounded-lg"
                  hideTextPlaceholders
                  orientation="portrait"
                  key={i}
                />
              ))
          : nowStreamingProjects?.map((item) => {
              if (!item) return null
              if (!item?.image?.cloudinaryPath) return null
              const title = item.project.name ?? undefined

              return (
                <div key={item.id}>
                  <div
                    className={classNames('mb-2 mr-4', {
                      'cursor-pointer duration-300 ease-in-out hover:z-10 md:hover:scale-[1.05]': landing,
                    })}
                    onClick={() => handleCardClick(item)}
                  >
                    <Image
                      aria-label={title}
                      alt={title}
                      loading="lazy"
                      sizes="(min-width: 1024px) 300px, (min-width: 768px) 250px, 200px"
                      src={getCloudinaryImageUrl({
                        path: item?.image?.cloudinaryPath,
                        width: 300,
                        height: 450,
                      })}
                      height={450}
                      width={300}
                      className="rounded-lg bg-black"
                    />
                  </div>
                </div>
              )
            })}
      </HorizontalItemScroller>
    </div>
  )
}
